import { CheckCircle, Users, Award, Clock, Lightbulb, Target, Shield, Calendar, Zap, Heart } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import EditableText from './cms/EditableText';

const AboutSection = () => {
  const { t, language } = useLanguage();

  // Access the translations object directly for arrays
  const translations = {
    en: {
      valuesBullets: [
        'Clarity over jargon',
        'Outcome before output',
        'Security by design',
        'Respect for timelines & budgets',
        'Innovation with purpose',
        'Client success first'
      ]
    },
    hu: {
      valuesBullets: [
        'Átláthatóság és nyílt kommunikácó',
        'Egyszerű megoldások komplex problémákra',
        'IT biztonság és adatvédelem',
        'Tisztelet a határidők és költségvetések iránt',
        'Céltudatos innováció',
        'Az ügyfél sikere az első'
      ]
    }
  };
  
  const highlights = [
    {
      icon: Users,
      titleKey: "about.expertTeam",
      descriptionKey: "about.expertTeamDesc"
    },
    {
      icon: Award,
      titleKey: "about.provenResults",
      descriptionKey: "about.provenResultsDesc"
    },
    {
      icon: Clock,
      titleKey: "about.agileDelivery",
      descriptionKey: "about.agileDeliveryDesc"
    }
  ];

  const achievements = [
    "about.yearsExperience",
    "about.clientSatisfaction", 
    "about.cuttingEdge",
    "about.multilingualSupport",
  ];

  const values = translations[language].valuesBullets;
  
  const valueIcons = [
    Lightbulb, // Clarity over jargon
    Target,    // Outcome before output
    Shield,    // Security by design
    Calendar,  // Respect for timelines & budgets
    Zap,       // Innovation with purpose
    Heart      // Client success first
  ];

  return (
    <section id="about" className="py-24 px-4 relative">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="float absolute top-20 right-10 w-40 h-40 bg-accent/10 rounded-full blur-3xl"></div>
        <div className="float absolute bottom-20 left-10 w-32 h-32 bg-primary/10 rounded-full blur-2xl" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-display font-bold mb-6">
            <EditableText k="about.whyChoose" /> <EditableText k="about.commodIT" className="gradient-text" />?
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            <EditableText k="about.notJustTech" />
          </p>
        </div>

        {/* Mission Statement - Centered */}
        <div className="text-center max-w-4xl mx-auto mb-20">
          <h3 className="text-3xl font-display font-bold mb-6 text-foreground">
            <EditableText k="about.yourSuccess" />
          </h3>
          <p className="text-lg text-muted-foreground leading-relaxed">
            <EditableText k="about.buildingPartnerships" />
          </p>
        </div>

        {/* Cards Grid - Centered Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto mb-20">
          {/* Achievements Card */}
          <div className="bg-gradient-card border border-primary/10 rounded-3xl p-8 glow-subtle group hover:glow-primary transition-all duration-300">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent/10 group-hover:bg-accent/20 transition-smooth mb-4">
                <Award className="w-8 h-8 text-accent" />
              </div>
              <h4 className="text-2xl font-display font-bold text-foreground mb-2">
                <EditableText k="about.whatSetsUsApart" />
              </h4>
            </div>
            <div className="space-y-4">
              {achievements.map((achievement) => (
                <div key={achievement} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                  <span className="text-foreground"><EditableText k={achievement} /></span>
                </div>
              ))}
            </div>
            
            {/* Satisfaction Badge */}
            <div className="mt-6 pt-6 border-t border-primary/10">
              <div className="text-center">
                <div className="text-4xl font-bold gradient-text mb-2">100%</div>
                <div className="text-muted-foreground"><EditableText k="about.clientSatisfactionBadge" /></div>
              </div>
            </div>
          </div>

          {/* Values Card */}
          <div className="bg-gradient-card border border-primary/10 rounded-3xl p-8 glow-subtle group hover:glow-primary transition-all duration-300">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 group-hover:bg-primary/20 transition-smooth mb-4">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <h4 className="text-2xl font-display font-bold text-foreground mb-2">
                <EditableText k="about.ourValues" />
              </h4>
            </div>
            <div className="space-y-4">
              {values.map((value: string, index: number) => {
                const IconComponent = valueIcons[index];
                return (
                  <div key={index} className="flex items-center gap-3">
                    <div className="p-1.5 rounded-lg bg-primary/10">
                      <IconComponent className="w-4 h-4 text-primary flex-shrink-0" />
                    </div>
                    <span className="text-foreground">{value}</span>
                  </div>
                );
              })}
            </div>
            
            {/* Values Motto */}
            <div className="mt-6 pt-6 border-t border-primary/10">
              <div className="text-center">
                <div className="text-lg font-semibold gradient-text mb-1">
                  <EditableText k="about.valuesMotto" />
                </div>
                <div className="text-sm text-muted-foreground">
                  <EditableText k="about.valuesMottoDesc" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Highlights - Linear Display */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {highlights.map((highlight, index) => (
            <div 
              key={highlight.titleKey} 
              className="bg-gradient-card border border-primary/10 rounded-3xl p-8 glow-subtle group hover:glow-primary transition-all duration-300"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="p-4 rounded-2xl bg-primary/10 group-hover:bg-primary/20 transition-smooth">
                  <highlight.icon className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-foreground mb-3">
                    <EditableText k={highlight.titleKey} />
                  </h4>
                  <p className="text-muted-foreground">
                    <EditableText k={highlight.descriptionKey} />
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;